import { Box, Grid, Typography, TextField, Tabs, Tab } from '@mui/material';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { UserGrid } from '../../components/UserGrid/UserGrid';
import { ProgramOfficeGrid } from '../../components/ProgramOfficeGrid/ProgramOfficeGrid';
import { UserEditForm } from '../../components/UserEditForm/UserEditForm';
import { ProgramOfficeEditForm } from '../../components/ProgramOfficeEditForm/ProgramOfficeEditForm';
import { EventGridCompact } from '../../components/EventGrid/EventGridCompact';
import { User, ProgramOffice } from '../../services/Types';
import { updateSearchParams } from '../../utils/urlParams';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`accounts-tabpanel-${index}`}
      aria-labelledby={`accounts-tab-${index}`}
      style={{ width: '100%' }}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `accounts-tab-${index}`,
    'aria-controls': `accounts-tabpanel-${index}`,
  };
}

export const Accounts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize search text from URL
  const [userSearchText, setUserSearchText] = useState<string>(() => {
    return searchParams.get('q') || searchParams.get('search') || '';
  });
  const [userAccountSelected, setUserAccountSelected] = useState<User>({} as User);
  const [programOfficeAccountSelected, setProgramOfficeAccountSelected] = useState<ProgramOffice>({} as ProgramOffice);
 
  // Determine active tab based on URL
  const [tabValue, setTabValue] = useState(location.pathname === '/admin/accounts/programOffices' ? 1 : 0);

  // Update URL params when search text changes
  const updateURLParams = useCallback((updates: Record<string, string | null | undefined>) => {
    const newParams = updateSearchParams(searchParams, updates);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync tab and clear search when navigating between User/Program Office via sidebar
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const isProgramOffices = location.pathname === '/admin/accounts/programOffices';
    setTabValue(isProgramOffices ? 1 : 0);

    if (prevPathRef.current !== location.pathname) {
      const wasUser =
        prevPathRef.current === '/admin/accounts/user' || prevPathRef.current === '/admin/accounts/';
      const wasProgramOffices = prevPathRef.current === '/admin/accounts/programOffices';
      const isNowUser =
        location.pathname === '/admin/accounts/user' || location.pathname === '/admin/accounts/';
      const isNowProgramOffices = location.pathname === '/admin/accounts/programOffices';

      if ((wasUser && isNowProgramOffices) || (wasProgramOffices && isNowUser)) {
        setUserSearchText('');
        updateURLParams({ q: null });
      }
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, updateURLParams]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setUserAccountSelected({} as User);
    setProgramOfficeAccountSelected({} as ProgramOffice);
    setUserSearchText('');
    setTabValue(newValue);
    // Clear search param when switching tabs
    updateURLParams({ q: null });
    navigate(newValue === 0 ? '/admin/accounts/user' : '/admin/accounts/programOffices');
  };

  // Quick filter text box handler
  const onUserFilterTextBoxChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setUserSearchText(value);
    updateURLParams({ q: value || null });
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h1">
          Accounts
        </Typography>
        <Box className="user-grid-header"
          sx={{
            display: 'flex',
            borderColor: 'divider',
            marginBottom: '10px',
          }}
        >
          <Box>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="User Accounts" {...a11yProps(0)} />
              <Tab label="Program Offices" {...a11yProps(1)} />
            </Tabs>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              height: '40px',
              marginLeft: 'auto',
            }}
          >
            <TextField
              type="text"
              id="user-filter-text-box"
              placeholder={tabValue === 0 ? "Search by name, po:program office or role:role" : "Search by office name or acronym"}
              value={userSearchText}
              onChange={onUserFilterTextBoxChanged}
              sx={{
                width: '450px',
              }}
            />
          </Box>
        </Box>
      </Grid>

      <TabPanel value={tabValue} index={0}>
        <Grid container>
          <Grid item xs={2}>
            <UserGrid searchText={userSearchText} setAccountSelected={setUserAccountSelected} />
          </Grid>
          <Grid item xs={10}>
            <Grid item xs={12}>
              <UserEditForm
                accountSelected={userAccountSelected}
                onSave={() => {}}
              />
            </Grid>
            <Grid item xs={12}>
              <EventGridCompact searchCriteria={userAccountSelected} />
            </Grid>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container>
          <Grid item xs={2}>
            <ProgramOfficeGrid searchText={userSearchText} setAccountSelected={setProgramOfficeAccountSelected} />
          </Grid>
          <Grid item xs={10}>
            <Grid item xs={12}>
              <ProgramOfficeEditForm
                accountSelected={programOfficeAccountSelected}
                onSave={() => {}}
              />
            </Grid>
            <Grid item xs={12}>
              <EventGridCompact searchCriteria={programOfficeAccountSelected} />
            </Grid>
          </Grid>
        </Grid>
      </TabPanel>
    </Grid>
  );
}; 